import { PlayerError } from '@foundation-player/error-codes';
import { FetchResponse, FetchService } from '@foundation-player/fetch-service';
import { PlayerEvent } from '@foundation-player/runtime';
import type {
  Manifest,
  ManifestConfig,
  MediaConfig,
  Source,
  SourceTextTrack,
  StUSConfig,
} from '@foundation-player/types';
import { FoundationErrorEvent } from './event-emitter/unfiltered';
import { PlayerConfig } from './player-config';

type StUSSubtitle = {
  url: string;
  languageCode: string;
  language: string;
  outputSet: 'TTML_XML' | 'WEBVTT_TEXT';
};

type StUSSubtitleList = {
  subtitles?: StUSSubtitle[];
};

type StUSData = {
  name: string;
  sources: (Source & StUSSubtitleList)[];
  licenses: {
    variant: string;
    uri: {
      href: string;
      templated: boolean;
    };
    certificate: string;
    type: string;
  }[];
  thumbnails?: string | null;
};

export class StUSResolver {
  private lastRequest?: {
    time: number;
    urlTemplate?: string;
    placeholders?: Record<string, unknown>;
    url: string;
    headers: Record<string, string>;
  };

  private lastResponse?: {
    time: number;
    statusCode: number;
    body?: unknown;
    usedStUS: boolean;
  };

  constructor(
    private readonly playerConfig: PlayerConfig,
    private readonly fetchService: FetchService,
  ) {
    this.playerConfig = playerConfig;
    this.fetchService = fetchService;
  }

  async resolveMediaConfig(
    mediaConfig: MediaConfig<StUSConfig | ManifestConfig>,
    headers: HeadersInit,
  ): Promise<MediaConfig<ManifestConfig>> {
    const stusUrl = this.getStUSUrlForMediaConfig(mediaConfig as MediaConfig<StUSConfig>);
    const shouldUseStus = Boolean(stusUrl) && Boolean(mediaConfig.id);

    if (!shouldUseStus) {
      return mediaConfig as MediaConfig<ManifestConfig>;
    }

    try {
      const { manifests, thumbnailsUrl, vttTextTracks } = await this.fetchStUSManifest(headers, stusUrl);
      const resolvedMediaConfig: MediaConfig<ManifestConfig> = {
        ...mediaConfig,
        type: mediaConfig.type,
        manifests,
        thumbnails: thumbnailsUrl ? { url: thumbnailsUrl, preload: false } : undefined,
        textTracks: vttTextTracks,
      };

      return resolvedMediaConfig;
    } catch (error) {
      const message = `Failed to fetch StUS manifest for ${mediaConfig.id}`;
      const statusCode = error instanceof FetchResponse ? error.getStatus() : 0;
      const body = error instanceof FetchResponse ? await error.getBody() : undefined;
      // TODO: this has to be changed to the ErroCode type once we migrated to error codes >=1.2.0
      let errorCode: number;
      if (body === 'timeout') {
        errorCode = PlayerError.STUS_SCHEDULER_REQUEST_TIMED_OUT_43800.code;
      } else if (statusCode >= 400 && statusCode < 500) {
        errorCode = PlayerError.STUS_RETURNS_4XX_ERROR_42800.code;
      } else if (statusCode >= 500) {
        errorCode = PlayerError.STUS_RETURNS_5XX_ERROR_42810.code;
      } else {
        errorCode = PlayerError.STUS_UNAVAILABLE_41800.code;
      }

      this.lastResponse = {
        time: Date.now(),
        statusCode,
        body,
        usedStUS: false,
      };

      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message,
        originalEvent: error,
        code: errorCode,
        context: this.getDebugInformation(),
      });
    }
  }

  getDebugInformation(): Record<string, unknown> {
    return {
      request: this.lastRequest,
      response: this.lastResponse,
      playerConfig: this.playerConfig.streamingURLService,
    };
  }

  private getStUSUrlForMediaConfig(mediaConfig: MediaConfig<StUSConfig>): string {
    const urlTemplate = this.playerConfig.streamingURLService?.endpoints[mediaConfig.type]?.endpoint || '';
    const placeholders = {
      mediaId: mediaConfig.id,
    };
    const replacedUrl = this.replaceStUSUrlPlaceholders(urlTemplate, placeholders);

    this.lastRequest = {
      time: Date.now(),
      urlTemplate,
      placeholders,
      url: replacedUrl,
      headers: {},
    };

    if (!urlTemplate) {
      return '';
    }

    return replacedUrl;
  }

  private async fetchStUSManifest(
    headers: HeadersInit,
    stusUrl: string,
  ): Promise<{
    manifests: Manifest[];
    thumbnailsUrl: string | null | undefined;
    vttTextTracks: MediaConfig['textTracks'];
  }> {
    const options = {
      // 5s is too large for any normal usage, but since a lot of our publishing partner sites,
      // are filled to the brim with ads and tracking scripts, we have to set it to a high value,
      // because unfortunately they refuse to start the player *after* the site is ready,
      // but instead do it alongside the bogged down site.
      timeout: 5000,
      headers,
    };

    this.lastRequest = {
      ...this.lastRequest,
      time: Date.now(),
      url: stusUrl,
      headers: this.removeAuthToken(headers as Record<string, string>),
    };

    const response = await this.fetchService.get<StUSData[]>(stusUrl, options);
    const json = await response.getBody();
    this.lastResponse = {
      time: Date.now(),
      statusCode: response.getStatus(),
      body: json,
      usedStUS: true,
    };

    // We cannot guarantee that every manifest has thumbnails. If there are any, the thumbnail links are all identical, so we can just take the first one we find.
    const thumbnails = json.find((stus) => stus.thumbnails)?.thumbnails;

    // Our current expectation is that all nodes will contain the same subtitle, if they exist. We will search for the
    // first one and use that.
    let stusSubtitles: StUSSubtitle[] | undefined = [];
    if (json[0] && json[0].sources.length > 0) {
      stusSubtitles = json[0].sources.find((stusSource) => (stusSource.subtitles?.length || 0) > 0)?.subtitles;
    }

    return {
      manifests: this.mapStUSToManifest(json),
      thumbnailsUrl: thumbnails,
      vttTextTracks: this.filterAndMapVttStUSSubtitle(stusSubtitles),
    };
  }

  private filterAndMapVttStUSSubtitle(stusSubtitles: StUSSubtitle[] | undefined): SourceTextTrack[] {
    if (stusSubtitles === undefined) {
      return [];
    }

    return stusSubtitles
      .filter((subtitle) => subtitle.outputSet === 'WEBVTT_TEXT')
      .map((subtitle, idx) => {
        return {
          id: `vtt-${idx + 1}`,
          url: subtitle.url,
          label: subtitle.language,
          language: subtitle.languageCode,
          mimeType: 'text/vtt',
        };
      });
  }

  private removeAuthToken(headers?: Record<string, string>) {
    const cleanedMap: Record<string, string> = {};
    if (headers) {
      for (const [key, value] of Object.entries(headers)) {
        if (key !== 'x-auth-token') {
          cleanedMap[key] = value;
        }
      }
    }
    return cleanedMap;
  }

  private mapStUSToManifest(stusData: StUSData[]): Manifest[] {
    return stusData.map((stus) => {
      return {
        type: stus.name,
        licenseServers: stus.licenses.map((license) => {
          return {
            address: license.uri.href,
            encryptionType: license.type.toLowerCase(),
            certificate: license.certificate,
          };
        }),
        sources: stus.sources.map((source) => {
          return {
            ...source,
            priority: source.priority.toLowerCase(),
          };
        }),
      } as Manifest;
    });
  }

  private replaceStUSUrlPlaceholders(url: string, placeholders: { mediaId?: string }): string {
    return url.replace('{mediaId}', placeholders.mediaId || '').replace('{MEDIA_ID}', placeholders.mediaId || '');
  }
}
